// SafetyFormRouteUtils.js
// Helpers to parse and build canonical /home routes with optional del_id and open state

const VALID_TABS = new Set([
  "near_miss",
  "safety_audit",
  "incident_investigation",
  "safety_form_reporting",
  "sds_lookup",
]);

export function normalizeTab(tab) {
  return VALID_TABS.has(tab) ? tab : null;
}

export function parseSafetyFormRoute(pathname, search) {
  if (!pathname || !pathname.startsWith("/home")) return {};
  const parts = pathname.split("/").filter(Boolean); // ["home", ...]
  const seg = parts[1] || null;
  const tab = normalizeTab(seg || null);

  let nearMissId = null;
  let safetyAuditId = null;
  let incidentInvestigationId = null;

  if (parts.length >= 3) {
    const suffix = parts.slice(2).join("/");

    if (tab === "near_miss" || tab === "safety_form_reporting") {
      if (suffix.startsWith("near_miss_id=:"))
        nearMissId = suffix.slice("near_miss_id=:".length);
      else if (suffix.startsWith("near_miss_id="))
        nearMissId = suffix.slice("near_miss_id=".length);
    }

    if (tab === "safety_audit" || tab === "safety_form_reporting") {
      if (suffix.startsWith("safety_audit_id=:"))
        safetyAuditId = suffix.slice("safety_audit_id=:".length);
      else if (suffix.startsWith("safety_audit_id="))
        safetyAuditId = suffix.slice("safety_audit_id=".length);
    }

    if (tab === "incident_investigation" || tab === "safety_form_reporting") {
      if (suffix.startsWith("incident_investigation_id=:"))
        incidentInvestigationId = suffix.slice(
          "incident_investigation_id=:".length
        );
      else if (suffix.startsWith("incident_investigation_id="))
        incidentInvestigationId = suffix.slice(
          "incident_investigation_id=".length
        );
    }
  }

  const params = new URLSearchParams(search || "");

  if (!nearMissId && tab === "near_miss" && params.has("near_miss_id")) {
    nearMissId = params.get("near_miss_id") || null;
  }
  if (
    !safetyAuditId &&
    tab === "safety_audit" &&
    params.has("safety_audit_id")
  ) {
    safetyAuditId = params.get("safety_audit_id") || null;
  }
  if (
    !incidentInvestigationId &&
    tab === "incident_investigation" &&
    params.has("incident_investigation_id")
  ) {
    incidentInvestigationId = params.get("incident_investigation_id") || null;
  }

  let action = null; // e.g., "view" | "edit" | "new"
  if (params.has("action")) {
    const a = (params.get("action") || "").toLowerCase();
    if (a === "view" || a === "edit" || a === "new") action = a;
  }
  let open = false;
  if (params.has("open")) {
    const v = (params.get("open") || "").toLowerCase();
    open = v === "1" || v === "true" || v === "yes";
  }

  // Get formType for safety_form_reporting context
  let formType = null;
  if (tab === "safety_form_reporting" && params.has("formType")) {
    formType = params.get("formType") || null;
  }

  return {
    tab,
    nearMissId,
    safetyAuditId,
    incidentInvestigationId,
    action,
    open,
    formType,
  };
}

export function buildCanonicalSafetyFormPath({
  tab,
  nearMissId,
  safetyAuditId,
  incidentInvestigationId,
  action,
  open,
  formType, // Optional: specify which form type when in reporting context
}) {
  const normTab = normalizeTab(tab);
  const base = normTab ? `/home/${normTab}` : "/home";

  let path = base;

  // If we're in the safety_form_reporting tab, build path with formType parameter
  if (normTab === "safety_form_reporting") {
    if (formType === "near_miss" && nearMissId) {
      path = `${base}/near_miss_id=${nearMissId}`;
    } else if (formType === "safety_audit" && safetyAuditId) {
      path = `${base}/safety_audit_id=${safetyAuditId}`;
    } else if (formType === "incident_investigation" && incidentInvestigationId) {
      path = `${base}/incident_investigation_id=${incidentInvestigationId}`;
    }
  } else {
    // Original behavior for individual form tabs
    if (normTab === "near_miss" && nearMissId) {
      path = `${base}/near_miss_id=${nearMissId}`;
    } else if (normTab === "safety_audit" && safetyAuditId) {
      path = `${base}/safety_audit_id=${safetyAuditId}`;
    } else if (normTab === "incident_investigation" && incidentInvestigationId) {
      path = `${base}/incident_investigation_id=${incidentInvestigationId}`;
    }
  }

  const query = new URLSearchParams();
  if (action) query.set("action", action);
  if (open) query.set("open", "1");
  if (normTab === "safety_form_reporting" && formType) {
    query.set("formType", formType);
  }
  const qs = query.toString();
  return qs ? `${path}?${qs}` : `${path}`;
}

export function canonicalizeSafetyFormLocation(locationLike) {
  const pathname = locationLike?.pathname || "";
  const search = locationLike?.search || "";
  if (!pathname.startsWith("/home") || pathname === "/home") return null;
  const parsed = parseSafetyFormRoute(pathname, search);

  // SDS Lookup owns its own query params (?view=, ?q=); never rewrite them
  if (parsed.tab === "sds_lookup") return null;

  // Defaults logic per tab
  if (parsed.tab === "near_miss") {
    if (parsed.nearMissId) {
      if (!parsed.action) parsed.action = "edit";
      if (!parsed.open) parsed.open = true;
    } else if (parsed.action === "new") {
      parsed.open = true;
    }
  } else if (parsed.tab === "safety_audit") {
    if (parsed.safetyAuditId) {
      if (!parsed.action) parsed.action = "edit";
      if (!parsed.open) parsed.open = true;
    } else if (parsed.action === "new") {
      parsed.open = true;
    }
  } else if (parsed.tab === "incident_investigation") {
    if (parsed.incidentInvestigationId) {
      if (!parsed.action) parsed.action = "edit";
      if (!parsed.open) parsed.open = true;
    } else if (parsed.action === "new") {
      parsed.open = true;
    }
  }

  if (!parsed.tab) return null;
  const canonical = buildCanonicalSafetyFormPath(parsed);
  const current = `${pathname}${search || ""}`;
  return canonical !== current ? canonical : null;
}

export function getDeepLinkedNearMissId(locationLike, expectedTab) {
  const pathname = locationLike?.pathname || "";
  const search = locationLike?.search || "";
  const { tab, nearMissId } = parseSafetyFormRoute(pathname, search);
  if (tab && expectedTab && normalizeTab(expectedTab) === tab) {
    return nearMissId || null;
  }
  return null;
}
